import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import { useNavigate, Link } from "react-router-dom";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MuiAvatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import "../i18n/i18n";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useColorScheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { getCurrentTenant } from "../config/tenantConfig";

const { apiBaseUrl: API_BASE_URL } = getCurrentTenant();

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  width: 28,
  height: 28,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.secondary,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
}));

const RegisterContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(206, 3.80%, 63.70%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 71.10%, 51.20%, 0.50), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function RegisterForm(props) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { tenantConfig } = props;

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] =
    React.useState("");

  const [language, setLanguage] = React.useState(i18n.language || "tr");
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [dialogMessage, setDialogMessage] = React.useState("");

  // Multi-step form states
  const [currentStep, setCurrentStep] = React.useState(1);
  const [email, setEmail] = React.useState("");
  const [emailVerificationCode, setEmailVerificationCode] = React.useState("");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [smsVerificationCode, setSmsVerificationCode] = React.useState("");
  const [isEmailVerified, setIsEmailVerified] = React.useState(false);
  const [isSmsVerified, setIsSmsVerified] = React.useState(false);

  const showDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };

  // Force MUI color mode to light
  function SystemModeSync() {
    const { setMode } = useColorScheme();
    React.useEffect(() => {
      setMode("light");
    }, [setMode]);
    return null;
  }

  // Clear localStorage on component mount
  React.useEffect(() => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("adminId");
      localStorage.removeItem("objectData");
      localStorage.removeItem("enumData");
      localStorage.removeItem("userRoles");
      localStorage.removeItem("projectCode");
      localStorage.removeItem("companyName");
    } catch (e) {
      console.warn("localStorage clear failed", e);
    }
  }, []);

  React.useEffect(() => {
    if (i18n.language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  const isSubdomain = window.location.hostname.split(".").length > 2;
  const shouldFetchProjectCodes = !isSubdomain || !tenantConfig?.projectCode;

  // Flag imports
  const turkeyFlag = require("../assets/turkey.png");
  const ukFlag = require("../assets/united-kingdom.png");
  const germanFlag = require("../assets/german.png");
  const frenchFlag = require("../assets/france.png");
  const spanishFlag = require("../assets/es.png");

  React.useEffect(() => {
    if (!shouldFetchProjectCodes) {
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [shouldFetchProjectCodes]);

  // Determine tenant logo
  const tenantLogoLight =
    tenantConfig?.logoLight || tenantConfig?.theme?.logoLight || null;
  const tenantLogoDark =
    tenantConfig?.logoDark || tenantConfig?.theme?.logoDark || null;
  const resolvedLogoSrc =
    theme.palette.mode === "dark"
      ? tenantLogoDark || tenantLogoLight
      : tenantLogoLight || tenantLogoDark;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const userData = {
      email: email, // Use the verified email
      phoneNumber: phoneNumber, // Include the verified phone number
      password: formData.get("password"),
    };

    if (!isEmailVerified || !isSmsVerified) {
      showDialog("Hata", "Lütfen e-posta ve SMS doğrulamalarını tamamlayın.");
      return;
    }

    setLoading(true);

    // TODO: Implement actual registration API call
    // For now, we'll simulate a registration request
    setTimeout(() => {
      setLoading(false);
      showDialog(
        "Kayıt Başarılı",
        "Hesabınız başarıyla oluşturuldu. Ana sayfaya yönlendiriliyorsunuz."
      );

      // Redirect to root URL after successful registration
      setTimeout(() => {
        // Store necessary data in localStorage
        localStorage.setItem("authToken", "dummy-token"); // Replace with actual token
        localStorage.setItem("email", userData.email);
        navigate("/");
      }, 2000);
    }, 1000);

    console.log("Registration data:", userData);
  };

  const validateInputs = () => {
    const password = document.getElementById("password");
    const confirmPassword = document.getElementById("confirmPassword");
    let isValid = true;

    if (password.value !== confirmPassword.value) {
      setConfirmPasswordError(true);
      setConfirmPasswordErrorMessage("Şifreler eşleşmiyor");
      isValid = false;
    } else {
      setConfirmPasswordError(false);
      setConfirmPasswordErrorMessage("");
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <SystemModeSync />
      <CssBaseline enableColorScheme />
      <RegisterContainer direction="column" justifyContent="space-between">
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 2,
            paddingRight: 1,
          }}
        >
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={(event, newLanguage) => {
              if (newLanguage !== null) {
                setLanguage(newLanguage);
                i18n.changeLanguage(newLanguage);
                // Mark this as an explicit user choice so the RCU English
                // default no longer applies on subsequent loads.
                localStorage.setItem("langSelected", "1");
              }
            }}
            aria-label="language selection"
            size="small"
            sx={{
              height: 36,
              "& .MuiToggleButtonGroup-grouped": {
                minWidth: "32px",
                padding: "4px 6px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                "&:not(:first-of-type)": {
                  borderLeft: "1px solid",
                  borderLeftColor: theme.palette.divider,
                  borderRadius: 0,
                },
                "&:first-of-type": {
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                },
                "&:last-of-type": {
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            }}
          >
            <ToggleButton value="tr" aria-label="Turkish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Turkish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={turkeyFlag}
                    alt="Turkish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  TR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="en" aria-label="English">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="English" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={ukFlag}
                    alt="UK Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  EN
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="de" aria-label="German">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="German" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={germanFlag}
                    alt="German Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  DE
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="fr" aria-label="French">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="French" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={frenchFlag}
                    alt="French Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  FR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="es" aria-label="Spanish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Spanish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={spanishFlag}
                    alt="Spanish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  ES
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Card variant="outlined">
          {tenantConfig?.name && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {resolvedLogoSrc ? (
                <Box
                  component="img"
                  src={resolvedLogoSrc}
                  alt={`${tenantConfig.name} Logo`}
                  sx={{
                    height: 70,
                    mr: 2,
                  }}
                />
              ) : (
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? "#FFF"
                        : theme.palette.primary.main,
                    textShadow:
                      theme.palette.mode === "dark"
                        ? "0px 2px 4px rgba(0, 0, 0, 0.2)"
                        : "none",
                    transition: "color 0.3s ease",
                    ...(tenantConfig.textStyle || {}),
                  }}
                >
                  {tenantConfig.name}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="h4"
            sx={{ textAlign: "center", fontWeight: "bold", mb: 1 }}
          >
            Hesap Oluştur
          </Typography>
          {/* <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.secondary", mb: 2 }}
          >
            <Link
              to="/login"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "none",
                fontWeight: "bold",
              }}
            >
              Giriş Yap
            </Link>
          </Typography> */}

          <Divider />

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            {currentStep === 1 && (
              <FormControl>
                <FormLabel htmlFor="email">E-posta</FormLabel>
                <TextField
                  error={emailError}
                  helperText={emailErrorMessage}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  color={emailError ? "error" : "primary"}
                />
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={async () => {
                    if (!email || !/\S+@\S+\.\S+/.test(email)) {
                      setEmailError(true);
                      setEmailErrorMessage(
                        "Geçerli bir e-posta adresi giriniz"
                      );
                      return;
                    }
                    setLoading(true);
                    // TODO: Implement send email verification code
                    setTimeout(() => {
                      setLoading(false);
                      setCurrentStep(2);
                    }, 1000);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "E-posta Doğrulama Kodu Gönder"
                  )}
                </Button>
              </FormControl>
            )}

            {currentStep === 2 && (
              <FormControl>
                <FormLabel htmlFor="emailVerificationCode">
                  E-posta Doğrulama Kodu
                </FormLabel>
                <TextField
                  id="emailVerificationCode"
                  type="text"
                  value={emailVerificationCode}
                  onChange={(e) => setEmailVerificationCode(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                />
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={async () => {
                    if (!emailVerificationCode) {
                      return;
                    }
                    setLoading(true);
                    // TODO: Implement verify email code
                    setTimeout(() => {
                      setLoading(false);
                      setIsEmailVerified(true);
                      setCurrentStep(3);
                    }, 1000);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "E-posta Doğrulama Kodunu Doğrula"
                  )}
                </Button>
              </FormControl>
            )}

            {currentStep === 3 && (
              <FormControl>
                <FormLabel htmlFor="phoneNumber">Telefon Numarası</FormLabel>
                <TextField
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="+90 5XX XXX XX XX"
                />
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={async () => {
                    if (!phoneNumber) {
                      return;
                    }
                    setLoading(true);
                    // TODO: Implement send SMS verification code
                    setTimeout(() => {
                      setLoading(false);
                      setCurrentStep(4);
                    }, 1000);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "SMS Doğrulama Kodu Gönder"
                  )}
                </Button>
              </FormControl>
            )}

            {currentStep === 4 && (
              <FormControl>
                <FormLabel htmlFor="smsVerificationCode">
                  SMS Doğrulama Kodu
                </FormLabel>
                <TextField
                  id="smsVerificationCode"
                  type="text"
                  value={smsVerificationCode}
                  onChange={(e) => setSmsVerificationCode(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                />
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={async () => {
                    if (!smsVerificationCode) {
                      return;
                    }
                    setLoading(true);
                    // TODO: Implement verify SMS code
                    setTimeout(() => {
                      setLoading(false);
                      setIsSmsVerified(true);
                      setCurrentStep(5); // Going directly to password step
                    }, 1000);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "SMS Doğrulama Kodunu Doğrula"
                  )}
                </Button>
              </FormControl>
            )}

            {currentStep === 5 && (
              <>
                <FormControl>
                  <FormLabel htmlFor="password">Şifre</FormLabel>
                  <TextField
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    name="password"
                    type="password"
                    id="password"
                    required
                    fullWidth
                    variant="outlined"
                    color={passwordError ? "error" : "primary"}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="confirmPassword">Şifre Tekrarı</FormLabel>
                  <TextField
                    error={confirmPasswordError}
                    helperText={confirmPasswordErrorMessage}
                    name="confirmPassword"
                    type="password"
                    id="confirmPassword"
                    required
                    fullWidth
                    variant="outlined"
                    color={confirmPasswordError ? "error" : "primary"}
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Hesap Oluştur"}
                </Button>
              </>
            )}
          </Box>
        </Card>
      </RegisterContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <DialogTitle id="dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography id="dialog-description">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            color="primary"
            autoFocus
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
